import { deploymentService } from './deploymentService';
import * as alpaca from './alpaca';

// Enhanced order interfaces that include strategy context
export interface IntelligentOrderRequest {
  // Basic order info
  symbol: string;
  quantity: number;
  side: 'buy' | 'sell';
  orderType: 'market' | 'limit' | 'stop' | 'stop_limit';
  limitPrice?: number;
  stopPrice?: number;
  timeInForce?: string;
  
  // CRITICAL: Trading mode selection (NEW - fixes hardcoded backend issue)
  tradingMode?: 'paper' | 'live';
  
  // Strategy context (the sophisticated part)
  strategyContext?: {
    strategyId?: string;
    models?: Array<{
      type: string;
      weight: number;
      confidence?: number;
      signal?: 'buy' | 'sell' | 'hold';
    }>;
    riskParameters?: {
      maxPositionSizePct: number;
      stopLossType: string;
      stopLossValue: string;
      profitTargetType: string;
      profitTargetValue: string;
      correlationThreshold: number;
      volatilityPeriod: number;
    };
    portfolioContext?: {
      totalCapital: number;
      currentExposure: number;
      existingPosition?: number;
      basketAllocation?: number;
    };
    executionSettings?: {
      executeRthOnly: boolean;
      maxSlippagePct: number;
      executionDelay: number;
      useVwap: boolean;
    };
  };
  
  // Metadata
  orderSource: 'manual' | 'strategy' | 'basket' | 'rebalance';
  parentStrategyId?: string;
  basketId?: string;
}

export interface IntelligentOrderResponse {
  success: boolean;
  orderId?: string;
  executedOrder?: any;
  adjustments?: {
    originalQuantity: number;
    finalQuantity: number;
    reason: string;
    riskAdjustments: string[];
    priceAdjustments?: Record<string, number>;
  };
  strategyImpact?: {
    newExposure: number;
    riskScore: number;
    recommendations: string[];
    portfolioBalance?: Record<string, number>;
    correlationMatrix?: Record<string, Record<string, number>>;
  };
  riskAssessment?: {
    approved: boolean;
    riskScore: number;
    adjustmentReason?: string;
    appliedRiskControls: string[];
    maxDrawdown?: number;
    sharpeRatio?: number;
    volatility?: number;
  };
  mlAnalysis?: {
    modelPredictions: Array<{
      modelType: string;
      signal: string;
      confidence: number;
      weight: number;
      prediction: number;
    }>;
    signalConfidence: number;
    featureImportances?: Record<string, number>;
    shapValues?: number[];
    recommendedAction: string;
    probabilityDistribution?: Record<string, number>;
  };
  message: string;
  executionTime?: number;
}

export interface PortfolioPosition {
  symbol: string;
  quantity: number;
  side: 'long' | 'short';
  averagePrice: number;
  currentPrice: number;
  unrealizedPnl: number;
  marketValue: number;
}

class IntelligentOrderService {
  private portfolioCache: Map<string, PortfolioPosition> = new Map();
  private activeStrategies: Map<string, any> = new Map();

  /**
   * Main intelligent order placement method
   * This is where ALL orders should go through to leverage our sophisticated strategy context
   */
  async placeIntelligentOrder(request: IntelligentOrderRequest): Promise<IntelligentOrderResponse> {
    try {
      console.log('IntelligentOrderService: Processing order with strategy context:', request);

      // Step 1: Risk Assessment & Position Sizing
      const riskAssessment = await this.assessOrderRisk(request);
      if (!riskAssessment.approved) {
        return {
          success: false,
          message: `Order rejected: ${riskAssessment.reason}`,
        };
      }

      // Step 2: Apply Intelligent Position Sizing
      const optimizedQuantity = this.calculateOptimalPosition(request, riskAssessment);
      
      // Step 3: ML Model Signal Integration
      const modelSignals = await this.getModelSignals(request);
      const signalConfidence = this.calculateSignalConfidence(modelSignals);
      
      // Step 4: Market Timing & Execution Strategy
      const executionStrategy = this.determineExecutionStrategy(request, signalConfidence);
      
      // Step 5: Build Enhanced Order
      const enhancedOrder = this.buildEnhancedOrder(request, {
        optimizedQuantity,
        executionStrategy,
        riskAssessment
      });

      // Step 6: Execute Order with Context
      const strategyContext = request.strategyContext;
      const executedOrder = await this.executeOrderWithContext(enhancedOrder, strategyContext, request);

      // Step 7: Update Strategy State
      if (request.parentStrategyId) {
        await this.updateStrategyState(request.parentStrategyId, executedOrder);
      }

      // Step 8: Portfolio Impact Analysis
      const portfolioImpact = await this.analyzePortfolioImpact(executedOrder);

      return {
        success: true,
        orderId: executedOrder.id,
        executedOrder: executedOrder,
        adjustments: {
          originalQuantity: request.quantity,
          finalQuantity: optimizedQuantity,
          reason: riskAssessment.adjustmentReason || 'Risk-optimized sizing',
          riskAdjustments: riskAssessment.appliedRiskControls || [],
          priceAdjustments: (executedOrder as any)._backend_adjustments?.priceAdjustments
        },
        strategyImpact: {
          ...portfolioImpact,
          portfolioBalance: (executedOrder as any)._strategy_impact?.portfolioBalance,
          correlationMatrix: (executedOrder as any)._strategy_impact?.correlationMatrix
        },
        riskAssessment: (executedOrder as any)._risk_assessment,
        mlAnalysis: (executedOrder as any)._ml_analysis,
        message: this.generateIntelligentMessage(executedOrder),
        executionTime: (executedOrder as any)._execution_time
      };

    } catch (error) {
      console.error('IntelligentOrderService: Error processing order:', error);
      return {
        success: false,
        message: `Order failed: ${error.message}`
      };
    }
  }

  /**
   * Risk assessment using strategy context
   */
  private async assessOrderRisk(request: IntelligentOrderRequest): Promise<{
    approved: boolean;
    reason?: string;
    adjustmentReason?: string;
    appliedRiskControls?: string[];
  }> {
    const riskControls: string[] = [];
    
    // Get current portfolio state
    const currentPositions = await this.getCurrentPositions();
    const portfolioValue = this.calculatePortfolioValue(currentPositions);
    
    // Check position size limits
    if (request.strategyContext?.riskParameters) {
      const maxPositionValue = portfolioValue * request.strategyContext.riskParameters.maxPositionSizePct;
      const orderValue = request.quantity * (await this.getCurrentPrice(request.symbol));
      
      if (orderValue > maxPositionValue) {
        riskControls.push(`Position size reduced to ${request.strategyContext.riskParameters.maxPositionSizePct * 100}% max`);
      }
    }

    // Check correlation limits
    if (request.strategyContext?.riskParameters?.correlationThreshold) {
      const correlationRisk = await this.checkCorrelationRisk(request.symbol, currentPositions);
      if (correlationRisk.isHighCorrelation) {
        riskControls.push('High correlation detected - position reduced');
      }
    }

    // Check volatility limits
    if (request.strategyContext?.riskParameters?.volatilityPeriod) {
      const volatilityRisk = await this.checkVolatilityRisk(request.symbol);
      if (volatilityRisk.isHighVolatility) {
        riskControls.push('High volatility - position size adjusted');
      }
    }

    return {
      approved: true, // Could be false for severe risk violations
      appliedRiskControls: riskControls,
      adjustmentReason: riskControls.length > 0 ? 'Risk management adjustments applied' : undefined
    };
  }

  /**
   * Calculate optimal position size using strategy parameters
   */
  private calculateOptimalPosition(request: IntelligentOrderRequest, riskAssessment: any): number {
    let optimalQuantity = request.quantity;

    // Apply portfolio percentage-based sizing
    if (request.strategyContext?.portfolioContext?.totalCapital && 
        request.strategyContext?.riskParameters?.maxPositionSizePct) {
      
      const maxPositionValue = request.strategyContext.portfolioContext.totalCapital * 
                               request.strategyContext.riskParameters.maxPositionSizePct;
      
      // Estimate current price (in real implementation, get from market data)
      const estimatedPrice = 100; // Placeholder - should use real price
      const maxQuantityByRisk = Math.floor(maxPositionValue / estimatedPrice);
      
      optimalQuantity = Math.min(optimalQuantity, maxQuantityByRisk);
    }

    // Apply basket allocation if this is part of a basket
    if (request.strategyContext?.portfolioContext?.basketAllocation) {
      optimalQuantity = Math.floor(optimalQuantity * request.strategyContext.portfolioContext.basketAllocation);
    }

    return Math.max(1, optimalQuantity); // Ensure at least 1 share
  }

  /**
   * Get ML model signals for the symbol
   */
  private async getModelSignals(request: IntelligentOrderRequest): Promise<Array<{
    modelType: string;
    signal: 'buy' | 'sell' | 'hold';
    confidence: number;
    weight: number;
  }>> {
    if (!request.strategyContext?.models) {
      return [];
    }

    // In a real implementation, this would call the ML model APIs
    // For now, return mock signals based on the configured models
    return request.strategyContext.models.map(model => ({
      modelType: model.type,
      signal: model.signal || 'hold',
      confidence: model.confidence || 0.5,
      weight: model.weight
    }));
  }

  /**
   * Calculate overall signal confidence from multiple models
   */
  private calculateSignalConfidence(signals: Array<{
    modelType: string;
    signal: 'buy' | 'sell' | 'hold';
    confidence: number;
    weight: number;
  }>): number {
    if (signals.length === 0) return 0.5;

    const weightedConfidence = signals.reduce((acc, signal) => {
      const signalStrength = signal.signal === 'buy' ? signal.confidence : 
                            signal.signal === 'sell' ? -signal.confidence : 0;
      return acc + (signalStrength * signal.weight);
    }, 0);

    const totalWeight = signals.reduce((acc, signal) => acc + signal.weight, 0);
    
    return totalWeight > 0 ? Math.abs(weightedConfidence / totalWeight) : 0.5;
  }

  /**
   * Determine execution strategy based on context
   */
  private determineExecutionStrategy(request: IntelligentOrderRequest, confidence: number): {
    orderType: string;
    executionTiming: string;
    slippageControl: boolean;
  } {
    const executionSettings = request.strategyContext?.executionSettings;
    
    return {
      orderType: confidence > 0.8 ? 'market' : 'limit', // High confidence = market order
      executionTiming: executionSettings?.executeRthOnly ? 'rth_only' : 'extended',
      slippageControl: executionSettings?.useVwap || false
    };
  }

  /**
   * Build enhanced order with all context
   */
  private buildEnhancedOrder(request: IntelligentOrderRequest, optimization: any) {
    return {
      // Basic order parameters
      symbol: request.symbol,
      qty: optimization.optimizedQuantity,
      side: request.side,
      type: request.orderType, // Use original order type, don't auto-convert
      time_in_force: request.timeInForce || 'day',
      
      // Enhanced metadata for tracking
      client_order_id: `${request.orderSource}_${Date.now()}`,
      order_class: request.strategyContext ? 'strategy' : 'simple',
      
      // Add limit price if needed
      ...(request.limitPrice && (request.orderType === 'limit' || request.orderType === 'stop_limit') 
          ? { limit_price: request.limitPrice } : {}),
      
      // Add stop price if needed  
      ...(request.stopPrice && (request.orderType === 'stop' || request.orderType === 'stop_limit')
          ? { stop_price: request.stopPrice } : {}),
      
      // Strategy context for backend processing
      extended_hours: optimization.executionStrategy.executionTiming === 'extended',
      
      // Risk management
      ...(request.strategyContext?.riskParameters?.stopLossValue ? {
        order_class: 'bracket',
        stop_loss: {
          stop_price: this.calculateStopLossPrice(request)
        }
      } : {}),
      
      ...(request.strategyContext?.riskParameters?.profitTargetValue ? {
        take_profit: {
          limit_price: this.calculateTakeProfitPrice(request)
        }
      } : {})
    };
  }

  /**
   * Execute enhanced order using VM trading service with proper fallbacks
   */
  private async executeOrderWithContext(
    enhancedOrder: any,
    strategyContext: any,
    originalRequest: IntelligentOrderRequest
  ): Promise<any> {
    console.log('IntelligentOrderService: Executing enhanced order:', enhancedOrder);
    
    // Prioritize deployment service which handles VM + fallback routing
    try {
      console.log('IntelligentOrderService: Using deployment service (VM + fallback routing)');
      
      // Fix the order data structure for consistent API usage
      const orderData: any = {
        symbol: enhancedOrder.symbol,
        qty: enhancedOrder.qty,
        side: enhancedOrder.side,
        type: originalRequest.orderType,
        time_in_force: enhancedOrder.time_in_force || 'day',
        
        // CRITICAL: Include trading mode from frontend selection
        metadata: {
          trading_mode: originalRequest.tradingMode || 'paper', // Default to paper for safety
          order_source: originalRequest.orderSource,
          strategy_id: originalRequest.parentStrategyId,
          frontend_selection: originalRequest.tradingMode
        }
      };
      
      // Add conditional price fields
      if (originalRequest.orderType === 'limit' && originalRequest.limitPrice) {
        orderData.limit_price = originalRequest.limitPrice;
      } else if (enhancedOrder.type === 'limit' && enhancedOrder.limit_price) {
        orderData.limit_price = enhancedOrder.limit_price;
      }
      
      if (originalRequest.orderType === 'stop' && originalRequest.stopPrice) {
        orderData.stop_price = originalRequest.stopPrice;
      }
      
      console.log('IntelligentOrderService: Sending order data with trading mode:', orderData);
      console.log(`IntelligentOrderService: Trading mode specified: ${originalRequest.tradingMode || 'NOT_SPECIFIED (defaulting to paper)'}`);
      
      // Log critical warning if trading mode not specified
      if (!originalRequest.tradingMode) {
        console.warn('⚠️  IntelligentOrderService: No trading mode specified in request! Defaulting to PAPER for safety.');
      } else {
        console.log(`✅ IntelligentOrderService: Trading mode explicitly specified as: ${originalRequest.tradingMode.toUpperCase()}`);
      }

      // Use deployment service which prioritizes VM and includes fallbacks
      const result = originalRequest.side === 'buy' 
        ? await deploymentService.placeBuyOrder(orderData)
        : await deploymentService.placeSellOrder(orderData);
      
      console.log('IntelligentOrderService: Order placed successfully via deployment service:', result);
      
      // Store the trade in the database for monitoring
      await this.storeTrade(result, originalRequest, strategyContext);
      
      return result;
      
    } catch (error) {
      console.error('IntelligentOrderService: Deployment service failed:', error);
              const errMsg = error instanceof Error ? error.message : String(error);
        throw new Error(`Order execution failed: ${errMsg}. Please ensure your VM is running and configured correctly.`);
    }
  }

  /**
   * Store trade in the database for activity monitoring
   */
  private async storeTrade(
    executionResult: any, 
    originalRequest: IntelligentOrderRequest, 
    strategyContext: any
  ): Promise<void> {
    try {
      // Import supabase here to avoid circular dependencies
      const { supabase } = await import('@/integrations/supabase/client');
      
      // Get current user
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user) {
        console.warn('IntelligentOrderService: Cannot store trade - user not authenticated');
        return;
      }

      const userId = session.user.id;
      
      // Determine strategy ID - use parentStrategyId for strategy orders, 'manual' for direct orders
      const strategyId = originalRequest.parentStrategyId || 'manual';
      
      // Calculate entry price from execution result with robust validation
      let entryPrice = 100; // Default fallback
      
      if (executionResult.filled_avg_price && !isNaN(parseFloat(executionResult.filled_avg_price))) {
        entryPrice = parseFloat(executionResult.filled_avg_price);
      } else if (executionResult.price && !isNaN(parseFloat(executionResult.price))) {
        entryPrice = parseFloat(executionResult.price);
      } else if (originalRequest.limitPrice && !isNaN(parseFloat(originalRequest.limitPrice.toString()))) {
        entryPrice = parseFloat(originalRequest.limitPrice.toString());
      }

      // Validate required fields
      if (!originalRequest.symbol || originalRequest.symbol.trim() === '') {
        console.error('IntelligentOrderService: Cannot store trade - invalid symbol');
        return;
      }

      if (!originalRequest.quantity || isNaN(originalRequest.quantity) || originalRequest.quantity <= 0) {
        console.error('IntelligentOrderService: Cannot store trade - invalid quantity');
        return;
      }

      if (isNaN(entryPrice) || entryPrice <= 0) {
        console.error('IntelligentOrderService: Cannot store trade - invalid entry price');
        return;
      }

      // Validate action (must be 'buy' or 'sell')
      const action = originalRequest.side?.toLowerCase();
      if (action !== 'buy' && action !== 'sell') {
        console.error('IntelligentOrderService: Cannot store trade - invalid action:', originalRequest.side);
        return;
      }

      // Map execution status to database enum (only 'open' or 'closed' allowed)
      const tradeStatus: 'open' | 'closed' = 'open'; // Always start as 'open'

      // Create trade record with validated data
      const tradeRecord = {
        user_id: userId,
        strategy_id: strategyId,
        symbol: originalRequest.symbol.trim().toUpperCase(),
        action: action as 'buy' | 'sell',
        quantity: Math.floor(originalRequest.quantity), // Ensure integer
        entry_price: Math.round(entryPrice * 100) / 100, // Round to 2 decimal places
        entry_timestamp: new Date().toISOString(),
        status: tradeStatus,
        metadata: {
          order_id: executionResult.id?.toString() || `manual_${Date.now()}`,
          order_type: originalRequest.orderType || 'market',
          execution_type: originalRequest.orderType || 'market', 
          time_in_force: originalRequest.timeInForce || 'day',
          order_source: originalRequest.orderSource || 'manual',
          limit_price: originalRequest.limitPrice || null,
          stop_price: originalRequest.stopPrice || null,
          strategy_context: strategyContext,
          backend_response: {
            execution_time: executionResult._execution_time || null,
            market_impact: executionResult.market_impact || null,
            execution_quality: executionResult.execution_quality || null
          }
        }
      };

      console.log('IntelligentOrderService: Storing validated trade record:', tradeRecord);

      const { data, error } = await supabase
        .from('trades')
        .insert(tradeRecord)
        .select('id')
        .single();

      if (error) {
        console.error('IntelligentOrderService: Error storing trade:', error);
        console.error('IntelligentOrderService: Failed trade record:', tradeRecord);
      } else {
        console.log('IntelligentOrderService: Trade stored successfully with ID:', data.id);
      }
    } catch (error) {
      console.error('IntelligentOrderService: Exception storing trade:', error);
      // Don't throw error here - trade execution was successful, storage is just for monitoring
    }
  }

  /**
   * Update strategy state after order execution
   */
  private async updateStrategyState(strategyId: string, executionResult: any) {
    try {
      // Update deployed strategy with new position
      const status = await deploymentService.getDeploymentStatus(strategyId);
      // In a real implementation, this would update the strategy's position tracking
      console.log(`Updated strategy ${strategyId} with new order ${executionResult.id}`);
    } catch (error) {
      console.warn('Failed to update strategy state:', error);
    }
  }

  /**
   * Analyze portfolio impact of the order
   */
  private async analyzePortfolioImpact(executionResult: any) {
    return {
      newExposure: 0.75, // Placeholder
      riskScore: 0.65,   // Placeholder
      recommendations: [
        'Position within risk limits',
        'Consider rebalancing in 24h',
        'Monitor correlation with existing positions'
      ]
    };
  }

  // Helper methods
  private async getCurrentPositions(): Promise<PortfolioPosition[]> {
    try {
      // Try to get positions from backend API first, but don't fail the entire order if this fails
      console.log('IntelligentOrderService: Attempting to get current positions...');
      const positions = await alpaca.positions.getPositions();
      return positions.map(pos => ({
        symbol: pos.symbol,
        quantity: parseInt(pos.qty),
        side: parseInt(pos.qty) > 0 ? 'long' : 'short',
        averagePrice: parseFloat(pos.avg_entry_price),
        currentPrice: parseFloat(pos.market_value) / Math.abs(parseInt(pos.qty)),
        unrealizedPnl: parseFloat(pos.unrealized_pl),
        marketValue: parseFloat(pos.market_value)
      }));
    } catch (error) {
      console.warn('Failed to get current positions, using empty portfolio for risk assessment:', error);
      // Return empty positions array rather than failing the entire order
      // This allows the order to proceed even if positions can't be fetched due to CORS/API issues
      return [];
    }
  }

  private calculatePortfolioValue(positions: PortfolioPosition[]): number {
    return positions.reduce((total, pos) => total + Math.abs(pos.marketValue), 0);
  }

  private async getCurrentPrice(symbol: string): Promise<number> {
    try {
      const quote = await alpaca.marketData.getLatestQuote(symbol);
      return (quote.bid + quote.ask) / 2;
    } catch (error) {
      console.warn(`Failed to get price for ${symbol}:`, error);
      return 100; // Fallback price
    }
  }

  private async checkCorrelationRisk(symbol: string, positions: PortfolioPosition[]): Promise<{
    isHighCorrelation: boolean;
    correlationScore: number;
  }> {
    // Placeholder - in real implementation, calculate correlations
    return {
      isHighCorrelation: false,
      correlationScore: 0.3
    };
  }

  private async checkVolatilityRisk(symbol: string): Promise<{
    isHighVolatility: boolean;
    volatilityScore: number;
  }> {
    // Placeholder - in real implementation, calculate volatility
    return {
      isHighVolatility: false,
      volatilityScore: 0.2
    };
  }

  private calculateStopLossPrice(request: IntelligentOrderRequest): number {
    const currentPrice = 100; // Placeholder
    const stopLossValue = parseFloat(request.strategyContext?.riskParameters?.stopLossValue || '2');
    
    if (request.side === 'buy') {
      return currentPrice * (1 - stopLossValue / 100);
    } else {
      return currentPrice * (1 + stopLossValue / 100);
    }
  }

  private calculateTakeProfitPrice(request: IntelligentOrderRequest): number {
    const currentPrice = 100; // Placeholder
    const profitTargetValue = parseFloat(request.strategyContext?.riskParameters?.profitTargetValue || '10');
    
    if (request.side === 'buy') {
      return currentPrice * (1 + profitTargetValue / 100);
    } else {
      return currentPrice * (1 - profitTargetValue / 100);
    }
  }

  /**
   * Create strategy context from basic order (for manual orders)
   */
  createBasicStrategyContext(portfolioValue: number = 100000): IntelligentOrderRequest['strategyContext'] {
    return {
      riskParameters: {
        maxPositionSizePct: 0.05, // 5% max position size
        stopLossType: 'percentage',
        stopLossValue: '2',
        profitTargetType: 'percentage',  
        profitTargetValue: '10',
        correlationThreshold: 0.7,
        volatilityPeriod: 20
      },
      portfolioContext: {
        totalCapital: portfolioValue,
        currentExposure: 0,
        existingPosition: 0
      },
      executionSettings: {
        executeRthOnly: true,
        maxSlippagePct: 0.5,
        executionDelay: 0,
        useVwap: false
      }
    };
  }

  /**
   * Generate intelligent message based on execution results
   */
  private generateIntelligentMessage(executionResult: any): string {
    const isIntelligent = executionResult._intelligent_backend;
    const mlAnalysis = executionResult._ml_analysis;
    const riskAssessment = executionResult._risk_assessment;
    
    if (!isIntelligent) {
      return "Order executed via standard processing";
    }
    
    let message = "Order executed with AI-enhanced analysis";
    
    if (mlAnalysis?.recommendedAction) {
      message += ` - ML recommendation: ${mlAnalysis.recommendedAction}`;
    }
    
    if (riskAssessment?.riskScore) {
      const riskLevel = riskAssessment.riskScore < 0.3 ? "Low" : 
                       riskAssessment.riskScore < 0.6 ? "Medium" : "High";
      message += ` - Risk level: ${riskLevel}`;
    }
    
    if (executionResult.execution_quality) {
      message += ` - Execution quality: ${executionResult.execution_quality}`;
    }
    
    return message;
  }
}

// Export singleton instance
export const intelligentOrderService = new IntelligentOrderService();
export default intelligentOrderService; 